import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaSpinner } from "react-icons/fa";
import "../css/Approval.css";
import VendorStatus from "./VendorStatus";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// if (!API_BASE_URL) throw new Error('API base URL not set. Please set REACT_APP_API_BASE_URL in .env');
const api = axios.create({ baseURL: 'https://django-backend-115705416374.asia-south1.run.app' });
//const api = axios.create({ baseURL: 'http://localhost:8000' });
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Helper function: get the absolute URL for a given doc path


function removeMediaPrefix(url) {
  if (!url) return "";
  // Remove everything up to and including the first '/media/'
  const idx = url.indexOf('/media/');
  if (idx >= 0) return url.slice(idx + '/media/'.length);
  return url.replace(/^\/?media\//, '');
}

function getSafeDocUrl(raw) {
  if (!raw) return "";
  let s = removeMediaPrefix(raw);
  try { s = decodeURIComponent(s); } catch { }
  // Fix single-slash scheme like 'https:/drive.google...' -> 'https://drive.google...'
  s = s.replace(/^(https?:)\/(?!\/)/i, '$1//');
  if (/^(https?:)?\/\//i.test(s) || s.startsWith('drive.google.com')) {
    return s.startsWith('http') ? s : ('https://' + s.replace(/^\/+/, ''));
  }
  return 'https://django-backend-115705416374.asia-south1.run.app/media/' + s.replace(/^media\/?/, '');
}

const Approval = () => {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [fromEmail, setFromEmail] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [fromSequence, setFromSequence] = useState("");
  // Re-upload mode state
  const [reuploadMode, setReuploadMode] = useState(false);
  const [bankKeyErrorOpen, setBankKeyErrorOpen] = useState(false); // 🆕 Bank Key Error Popup State
  const [reuploadSending, setReuploadSending] = useState(false);
  const [selectedDocKeys, setSelectedDocKeys] = useState(new Set());
  // Processing state to disable all buttons and show loader
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const res2 = await api.get('/api/approvals/my-tasks/');
      setTasks(res2.data || []);
    } catch (e) {
      setError("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const openTask = async (taskId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/approvals/${taskId}/`);
      setSelectedTask(res.data);
      setComment("");

      // 🆕 Check for Bank Key Failure and open popup
      // Note: vendor_profile might be null if no registration data exists
      // The modal should remain closed by default until the user clicks "⚠️ Failed (Click for details)"
      setBankKeyErrorOpen(false);
    } catch (e) {
      setError("Failed to load task detail");
    } finally {
      setLoading(false);
    }
  };
  const reassign = async () => {
    if (!selectedTask) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        from_email: fromEmail || (selectedTask.assignees?.[0]?.approver_email || ''),
        to_email: toEmail,
        from_sequence: fromSequence || selectedTask.node_sequence,
      };
      await api.post(`/api/approvals/${selectedTask.id}/reassign/`, payload);
      const res = await api.get(`/api/approvals/${selectedTask.id}/`);
      setSelectedTask(res.data);
      setReassignOpen(false);
      setSuccess('Approver reassigned successfully');
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to reassign approver';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };
  const act = async (action) => {
    if (isProcessing) return; // Prevent multiple clicks
    setIsProcessing(true);
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post(`/api/approvals/${selectedTask.id}/act/`, { action, comment });
      setSelectedTask(res.data);
      setSuccess(action === 'approve' ? 'Approved successfully' : 'Rejected successfully');
      await fetchTasks();
      // Redirect back to approval list after short delay
      setTimeout(() => {
        setSelectedTask(null);
        setIsProcessing(false);
      }, 500);
    } catch (e) {
      const msg = e?.response?.data?.error || 'Action failed';
      setError(msg);
      setIsProcessing(false);
    } finally {
      setLoading(false);
    }
  };
  const [panStatus, setPanStatus] = useState(
    selectedTask?.vendor_profile?.pan_tan_number_status || "Not Verified"
  )
  const [msmeStatus, setMsmeStatus] = useState(
    selectedTask?.vendor_profile?.msme_number_status || "Not Verified"
  );
  const [gstStatus, setGstStatus] = useState(
    selectedTask?.vendor_profile?.gst_number_status || "Not Verified"
  );

  useEffect(() => {
    if (selectedTask?.vendor_profile?.pan_tan_number_status) {
      setPanStatus(selectedTask.vendor_profile.pan_tan_number_status);
      setMsmeStatus(selectedTask.vendor_profile.msme_number_status || "Not Verified");
      setGstStatus(selectedTask.vendor_profile.gst_number_status || "Not Verified");
    }
  }, [selectedTask]);

  const retryBankKey = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post('/api/admin/retry-bank-key/', { vendor_code: selectedTask.vendor?.vendor_code });
      setSuccess(res.data.message || 'Bank Key Retry Successful');
      setBankKeyErrorOpen(false);
      // Reload task details to update the UI
      await openTask(selectedTask.id);
    } catch (e) {
      const msg = e?.response?.data?.error || 'Bank Key Retry failed';
      setError(msg);
    } finally {
      setIsProcessing(false);
      setLoading(false);
    }
  };

  useEffect(() => { fetchTasks(); }, []);

  if (selectedTask) {
    const t = selectedTask;
    // Canonical document fields and labels
    const docFields = [
      { key: 'gst_file', label: 'GST File' },
      { key: 'pan_tan_file', label: 'PAN File' },
      { key: 'assessment_report', label: 'Assessment Report' },
      { key: 'nda_report', label: 'NDA Report' },
      { key: 'cancel_cheque', label: 'Cancelled Cheque' },
      { key: 'msme_certificate', label: 'MSME Certificate' },
      { key: 'passbook', label: 'Passbook' },
    ];

    const toggleDocKey = (key) => {
      setSelectedDocKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
    };

    const sendReuploadRequest = async () => {
      const doc_keys = Array.from(selectedDocKeys);
      if (doc_keys.length === 0 || isProcessing) return; // Prevent multiple clicks
      setIsProcessing(true);
      setReuploadSending(true);
      setError(null);
      setSuccess(null);
      try {
        await api.post('/api/vendor/request-reupload/', {
          task_id: t.id,
          vendor_code: t.vendor?.vendor_code,
          vendor_email: t.vendor?.vendor_email,
          doc_keys,
          comment: (comment || '').trim(),
        });
        setSuccess('Re-upload request sent to vendor');
        setReuploadMode(false);
        setSelectedDocKeys(new Set());
        await fetchTasks(); // Refresh tasks list
        // Redirect back to approval list after short delay
        setTimeout(() => {
          setSelectedTask(null);
          setIsProcessing(false);
        }, 500);
      } catch (e) {
        const msg = e?.response?.data?.error || 'Failed to send re-upload request';
        setError(msg);
        setIsProcessing(false);
      } finally {
        setReuploadSending(false);
      }
    };

    const renderField = (label, value, icon = '', status = null) => {
      // Keep your existing logic for GST and empty values
      if (label !== 'GST Number' && (!value || value.toString().trim() === '')) return null;

      return (
        <div className="apv-vendor-info-field" key={label}>
          <span className="apv-vendor-info-label">{label}</span>
          <span className="apv-vendor-info-value">
            {icon ? `${icon} ` : ''}{value || ''}
          </span>

          {/* New Status Logic */}
          {status && (
            <span
              className="apv-vendor-info-status">
              {status === 'verified' ? 'Verified' : 'Not Verified'}
            </span>
          )}
        </div>
      );
    };

    return (
      <div className="apv-detail">
        <div className="apv-detail-head">
          <button className="apv-btn apv-btn-secondary" onClick={() => setSelectedTask(null)}>
            ← Back
          </button>
          <h3 className="apv-detail-title">Approval Detail</h3>

        </div>
        {error && <div className="apv-alert apv-alert-error">{error}</div>}
        {success && <div className="apv-alert apv-alert-success">{success}</div>}

        <div className="apv-card apv-detail-card">
          <div className="apv-detail-header">
            <div className="apv-vendor-info">
              <div className="apv-vendor-name">{t.vendor?.vendor_name}</div>
              <div className="apv-vendor-email">{t.vendor?.vendor_email}</div>
            </div>
            <div className="apv-status-section">
              <div className="apv-company">{t.company}</div>
              <div className={`apv-status-pill ${t.status}`}>{t.status}</div>
            </div>
          </div>
          <div className="apv-detail-actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {/* {!reuploadMode && (
              <button
                className="apv-btn"
                onClick={() => { setReuploadMode(true); setSelectedDocKeys(new Set()); }}
                title="Request specific documents for re-upload"
              >
                Re-Upload
              </button>
            )} */}
            {/* {reuploadMode && (
              <>
                <button
                  className="apv-btn apv-btn-secondary"
                  onClick={() => { setReuploadMode(false); setSelectedDocKeys(new Set()); }}
                  disabled={reuploadSending}
                >
                  Cancel
                </button>
                <button
                  className="apv-btn"
                  onClick={sendReuploadRequest}
                  disabled={reuploadSending || selectedDocKeys.size === 0}
                  title={selectedDocKeys.size === 0 ? 'Select at least one document' : 'Send re-upload request'}
                >
                  {reuploadSending ? 'Sending...' : 'Send'}
                </button>
              </>
            )} */}
          </div>
        </div>

        {/* Vendor Profile Information */}
        {t.vendor_profile && (
          <div className="apv-card">
            <h4 className="apv-section-title center">Vendor Information</h4>
            {/* 2-column grid layout, each cell is a field or doc link */}
            <div className="apv-vendor-info-grid">
              {/* --- PERSONAL DETAILS --- */}
              {renderField('Full Name', `${t.vendor_profile.first_name} ${t.vendor_profile.last_name || ''}`, '🧑')}
              {renderField('Email', t.vendor_profile.email, '✉️')}
              {renderField('Phone', t.vendor_profile.phone, '📞')}
              {renderField('Contact Person', t.vendor_profile.contact_person_name)}
              {renderField('House Number', t.vendor_profile.house_number)}
              {renderField('District', t.vendor_profile.district)}

              <div className="apv-vendor-info-field">
                <span className="apv-vendor-info-label">Office Address</span>
                <span className="apv-vendor-info-value">
                  {t.vendor_profile.registered_office_address}
                  {t.vendor_profile.registered_office_address_ext ? `, ${t.vendor_profile.registered_office_address_ext}` : ''}
                </span>
              </div>

              {renderField('City', t.vendor_profile.city, '🏙️')}
              {renderField('Region', t.vendor_profile.region, '📍')}
              {renderField('Postal Code', t.vendor_profile.postal_code, '📮')}
              {renderField('Country', t.vendor_profile.country, '🌍')}

              {/* --- TAX & COMPLIANCE (GST Always) --- */}

              {/* GST number without tick option */}
              {/* <div className="apv-vendor-info-field">
                <span className="apv-vendor-info-label">GST Number</span>
                <span className="apv-vendor-info-value">{t.vendor_profile.gst_number || ''}</span>
                <span className="apv-vendor-info-status" style={{ color: t.vendor_profile.gst_number_status.length > 8 ? 'red' : 'green' }}>
                  {t.vendor_profile.gst_number_status}
                </span>
              </div> */}

              <div className="apv-vendor-info-field">
                <span className="apv-vendor-info-label">GST Number</span>
                <span className="apv-vendor-info-value">{t.vendor_profile.gst_number || ''}</span>
                <span
                  className="apv-vendor-info-status"
                  style={gstStatus === "Verified" ? { color: "green" } : { color: "red" }}
                >
                  {gstStatus}
                  {t.vendor_profile.gst_number && (
                    <input
                      type="checkbox"
                      checked={gstStatus === "Verified"}
                      onChange={(e) =>
                        setGstStatus(e.target.checked ? "Verified" : "Not Verified")
                      }
                      style={{ marginLeft: "8px" }}
                    />
                  )}
                </span>
              </div>




              {/* without using renderField to allow custom status styling and checkbox for PAN/TAN verification */}
              {/* <div className="apv-vendor-info-field">
                <span className="apv-vendor-info-label">PAN/TAN</span>
                <span className="apv-vendor-info-value">{t.vendor_profile.pan_tan_number || ''}</span>
                <span className="apv-vendor-info-status" style={{ color: t.vendor_profile.pan_tan_number_status.length > 8 ? 'red' : 'green' }}>
                  {t.vendor_profile.pan_tan_number_status}
                </span>
              </div> */}

              <div className="apv-vendor-info-field">
                <span className="apv-vendor-info-label">PAN/TAN</span>
                <span className="apv-vendor-info-value">{t.vendor_profile.pan_tan_number || ''}</span>
                <span
                  className="apv-vendor-info-status"
                  style={panStatus === "Verified" ? { color: "green" } : { color: "red" }}
                >
                  {panStatus}
                  <input
                    type="checkbox"
                    checked={panStatus === "Verified"}
                    onChange={(e) =>
                      setPanStatus(e.target.checked ? "Verified" : "Not Verified")
                    }
                    style={{ marginLeft: "8px" }}
                  />
                </span>
              </div>

              {/* {renderField('PAN/TAN', t.vendor_profile.pan_tan_number,t.vendor_profile.pan_tan_number_status)} */}
              {renderField('VAT Number', t.vendor_profile.vat_number)}
              {renderField('CIN Number', t.vendor_profile.cin_number)}
              {renderField('TIN Number', t.vendor_profile.tin_number)}
              {/* {renderField('MSME Number', t.vendor_profile.msme_number, '🏭',t.vendor_profile.msme_number_status)} */}

              {/* MSME number without tick Option */}
              {/* <div className="apv-vendor-info-field">
                <span className="apv-vendor-info-label">MSME Number</span>
                <span className="apv-vendor-info-value">{t.vendor_profile.msme_number || ''}</span>
                <span className="apv-vendor-info-status" style={{ color: t.vendor_profile.msme_number_status.length > 8 ? 'red' : 'green' }}>
                  {t.vendor_profile.msme_number_status}
                </span>
              </div> */}

              <div className="apv-vendor-info-field">
                <span className="apv-vendor-info-label">MSME Number</span>
                <span className="apv-vendor-info-value">{t.vendor_profile.msme_number || ''}</span>
                <span
                  className="apv-vendor-info-status"
                  style={msmeStatus === "Verified" ? { color: "green" } : { color: "red" }}
                >
                  {msmeStatus}
                  {t.vendor_profile.msme_number && (
                    <input
                      type="checkbox"
                      checked={msmeStatus === "Verified"}
                      onChange={(e) =>
                        setMsmeStatus(e.target.checked ? "Verified" : "Not Verified")
                      }
                      style={{ marginLeft: "8px" }}
                    />
                  )}
                </span>
              </div>

              {/* --- BANK DETAILS --- */}
              {renderField('Bank Name', t.vendor_profile.bank_name, '🏦')}
              {renderField('Bank Account Type', t.vendor_profile.bank_account_name)}
              {renderField('Bank Country', t.vendor_profile.bank_country)}
              {renderField('Bank State', t.vendor_profile.bank_state)}
              {renderField('Bank Address', t.vendor_profile.bank_address)}
              {renderField('Bank City', t.vendor_profile.bank_city)}
              {renderField('Bank Branch', t.vendor_profile.bank_branch)}
              {renderField('Account Number', t.vendor_profile.account_number, '🏦')}
              {renderField('Account Holder', t.vendor_profile.account_holder_name, '👤')}

              {/* Dynamic Swift/IFSC */}
              {renderField(
                (t.vendor_profile.bank_country === 'IN' || !t.vendor_profile.bank_country) ? 'IFSC Code' : 'SWIFT Code',
                t.vendor_profile.ifsc_code,
                '🏦'
              )}

              {renderField('IBAN Number', t.vendor_profile.iban_number)}

              {/* Bank Key Verification Status Display (Only shown if failed) */}
              {t.vendor_profile.bank_key_verification_status === 'failed' && (
                <div className="apv-vendor-info-field">
                  <span className="apv-vendor-info-label">Bank Key Status</span>
                  <span className="apv-vendor-info-value">
                    <span style={{ color: 'red', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setBankKeyErrorOpen(true)}>⚠️ Failed (Click for details)</span>
                  </span>
                </div>
              )}

              {/* --- DOCUMENTS --- */}
              {docFields
                .filter(f => t.vendor_profile && t.vendor_profile[f.key])
                .map(f => (
                  <div className="apv-vendor-info-field" key={f.key}>
                    <span className="apv-vendor-info-label"></span>
                    <span className="apv-vendor-info-value" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {reuploadMode && (
                        <input
                          type="checkbox"
                          checked={selectedDocKeys.has(f.key)}
                          onChange={() => toggleDocKey(f.key)}
                          title={`Select ${f.label} for re-upload`}
                        />
                      )}
                      <a
                        className="apv-document-link"
                        href={getSafeDocUrl(t.vendor_profile[f.key])}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open ${f.label}`}
                      >
                        📄 {f.label}
                      </a>
                      {/* show per-doc reupload status if any */}
                      {!!t.doc_status && t.doc_status[f.key] && (
                        <span className={`apv-chip ${t.doc_status[f.key]}`}>{t.doc_status[f.key]}</span>
                      )}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="apv-card">
          <h4 className="apv-section-title center">Assignees</h4>
          <div className="apv-assignees">
            {(t.assignees || []).map(a => (
              <div key={a.id} className={`apv-chip ${a.state}`}>
                <span className="apv-chip-email">{a.approver_email}</span>
                <span className="apv-chip-state">{a.state}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="apv-action-section">
          <div className="apv-comment-section">
            <label className="apv-comment-label">Comments (Required for Reject)</label>
            <textarea
              className="apv-comment-input"
              placeholder="Add your comments here..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              disabled={isProcessing}
            />
          </div>
          <div className="apv-action-buttons">
            {/* Re-Upload controls on the left */}
            <div style={{ marginRight: 'auto', display: 'flex', gap: 12 }}>
              {!reuploadMode && (
                <button
                  className="apv-action-btn apv-reupload-btn"
                  type="button"
                  onClick={() => { setReuploadMode(true); setSelectedDocKeys(new Set()); }}
                  title="Request specific documents for re-upload"
                  disabled={isProcessing}
                >
                  ↻ Re-Upload
                </button>
              )}
              {reuploadMode && (
                <>
                  <button
                    className="apv-action-btn apv-reupload-cancel"
                    type="button"
                    onClick={() => { setReuploadMode(false); setSelectedDocKeys(new Set()); }}
                    disabled={isProcessing || reuploadSending}
                  >
                    Cancel
                  </button>
                  <button
                    className="apv-action-btn apv-reupload-send"
                    type="button"
                    onClick={sendReuploadRequest}
                    disabled={isProcessing || reuploadSending || selectedDocKeys.size === 0}
                    title={selectedDocKeys.size === 0 ? 'Select at least one document' : 'Send re-upload request'}
                  >
                    {reuploadSending ? 'Sending...' : 'Send'}
                  </button>
                </>
              )}
            </div>
            <button
              className="apv-action-btn apv-approve-btn"
              disabled={isProcessing || loading || (selectedTask?.approve_disabled_reason ? true : false)}
              onClick={() => act('approve')}
            >
              <span className="apv-btn-icon">✓</span>
              {selectedTask?.approve_disabled_reason ? `Approve (disabled: ${selectedTask.approve_disabled_reason})` : 'Approve'}
            </button>
            <button
              className="apv-action-btn apv-reject-btn"
              disabled={isProcessing || loading || !comment.trim()}
              onClick={() => act('reject')}
            >
              <span className="apv-btn-icon">✗</span>
              Reject
            </button>
          </div>
        </div>
        {reassignOpen && (
          <div className="apv-modal">
            <div className="apv-modal-content">
              <h4>Reassign Approver</h4>
              <div className="apv-modal-row">
                <label>From Email</label>
                <input type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder={t.assignees?.[0]?.approver_email || ''} />
              </div>
              <div className="apv-modal-row">
                <label>To Email</label>
                <input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="new.approver@company.com" />
              </div>
              <div className="apv-modal-row">
                <label>From Sequence</label>
                <input type="number" value={fromSequence} onChange={(e) => setFromSequence(e.target.value)} />
              </div>
              <div className="apv-modal-actions">
                <button className="apv-btn apv-btn-secondary" onClick={() => setReassignOpen(false)}>Cancel</button>
                <button className="apv-btn" disabled={loading || !toEmail} onClick={reassign}>Confirm</button>
              </div>
            </div>
          </div>
        )}

        {/* Middle screen loader popup */}
        {isProcessing && (
          <div className="fullscreen-loader-overlay">
            <div className="fullscreen-loader-content">
              <FaSpinner className="spinner" style={{ fontSize: '48px', color: '#667eea' }} />
              <p style={{ marginTop: '20px', fontSize: '18px', color: '#333', fontWeight: 500 }}>
                Please Wait While We Process Your Request
              </p>
            </div>
          </div>
        )}

        {/* 🆕 Bank Key Failure Modal */}
        {bankKeyErrorOpen && (
          <div className="apv-modal">
            <div className="apv-modal-content" style={{ maxWidth: '500px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4 style={{ color: '#e53e3e', margin: 0 }}>⚠️ Bank Key Creation Failed</h4>
                <button onClick={() => setBankKeyErrorOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
              </div>
              <div style={{ marginBottom: '20px', color: '#2d3748' }}>
                <p><strong>Attention:</strong> The automatic Bank Key creation/verification for this vendor failed during registration.</p>
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '4px', color: '#c53030', fontSize: '0.9rem' }}>
                  <strong>Error Details:</strong><br />
                  {t.vendor_profile?.bank_key_error || "Unknown error"}
                </div>
                <p style={{ marginTop: '15px', fontSize: '0.9rem', color: '#718096' }}>
                  You must address the Bank Key error before the final approval step. You can retry the automatic creation below, or manage it in S/4 HANA directly if the retry fails (IFSC: {t.vendor_profile?.ifsc_code}).
                </p>
              </div>
              <div className="apv-modal-actions" style={{ justifyContent: 'space-between' }}>
                <button className="apv-btn apv-btn-secondary" onClick={() => setBankKeyErrorOpen(false)}>Acknowledge & Close</button>
                <button className="apv-btn" style={{ backgroundColor: '#ff9800' }} disabled={isProcessing} onClick={retryBankKey}>
                  {isProcessing ? 'Retrying...' : 'Retry Bank Key Creation'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="apv-list">
      <div className="apv-list-header">
        <h2 className="apv-main-title">My Approvals</h2>
        <p className="apv-subtitle">Review and approve pending vendor registrations</p>
      </div>
      {error && <div className="apv-alert apv-alert-error">{error}</div>}
      {loading && <div className="apv-loading">Loading...</div>}
      <div className="apv-grid">
        {tasks.map(task => {
          return (
            <div key={task.id} className="apv-card apv-card-hover">
              <div className="apv-card-header">
                <div className="apv-vendor-info">
                  <div className="apv-vendor-name">{task.vendor?.vendor_name}</div>
                  <div className="apv-vendor-email">{task.vendor?.vendor_email}</div>
                </div>
                <div className={`apv-status-badge ${task.status}`}>{task.status}</div>
              </div>
              <div className="apv-card-content">
                <div className="apv-company-info">
                  <span className="apv-company-label">Company</span>
                  <span className="apv-company-name">{task.company}</span>
                </div>
                <div className="apv-time-info">
                  <span className="apv-time-label">Since</span>
                  <span className="apv-time-value">{new Date(task.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="apv-card-footer">
                <button className="apv-view-btn" onClick={() => openTask(task.id)}>
                  <span className="apv-btn-icon">👁️</span>
                  View Details
                </button>
              </div>
            </div>
          );
        })}
        {!loading && tasks.length === 0 && (
          <div className="apv-empty-state">
            <div className="apv-empty-icon">📋</div>
            <div className="apv-empty-title">No Pending Approvals</div>
            <div className="apv-empty-message">All caught up! No vendor registrations require your approval at the moment.</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Approval;